<script setup lang="ts">
import { computed, type VNode } from 'vue'
import UiNavLink from '../UiNavLink.vue'
import UiIcon, { type UiIconProps } from '../UiIcon.vue'
import UiText from '../UiText.vue'
import { useButtonBase } from './composables/useButtonBase'
import { useButtonToggle } from './composables/useButtonToggle'
import { 
    getVariantClasses, 
    getTransitionClasses, 
    getBackgroundHoverClasses, 
    getArrowClasses,
    getEffectiveHoverStyle,
    getPressedClasses,
    getActivePressedClasses,
} from './utils/buttonClasses'
import type { ButtonBaseProps, ButtonToggleProps, ButtonJustify } from './types'

export interface UiLinkButtonProps extends ButtonBaseProps, ButtonToggleProps {
    /** Text label */
    label?: string
    /** Icon name to show before label */
    icon?: UiIconProps['name']
    /** Show arrow icon after label */
    showArrow?: boolean
    /** Content justification */
    justify?: ButtonJustify
    /** Text variant to control text size (defaults to 'button') */
    textVariant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'p-small' | 'span' | 'caption' | 'button' | 'label' | 'subtle' | 'caption-normal' | 'tooltip' | 'override'
    /** Arrow color style - 'primary' (default) or 'inherit' to use current text color */
    arrowColor?: 'primary' | 'inherit'
    /** Hover decoration style - 'background' (default), 'underline', or 'none' */
    hoverDecoration?: 'background' | 'underline' | 'none'
}

const props = withDefaults(defineProps<UiLinkButtonProps>(), {
    variant: 'primary',
    theme: 'light',
    disabled: false,
    noHover: false,
    justify: 'center',
    textVariant: 'button',
    arrowColor: 'primary',
    hoverDecoration: 'background',
})

const model = defineModel<boolean | undefined>({ default: undefined })

const { isLink, isExternal, effectiveNoHover } = useButtonBase(props)

const { isToggled, isDisabled, handleClick, handleMouseEnter } = useButtonToggle({
    toggle: props.toggle,
    value: props.value,
    disabled: props.disabled,
    modelValue: model,
})

const effectiveVariant = computed(() => {
    if (isToggled.value && props.activeVariant) {
        return props.activeVariant
    }
    return props.variant
})

const effectiveHoverStyle = computed(() => {
    if (props.hoverStyle) {
        return props.hoverStyle
    }
    if (effectiveVariant.value === 'ghost') {
        return 'highlight'
    }
    return getEffectiveHoverStyle(undefined, effectiveVariant.value)
})

const dynamicClasses = computed(() => {
    const noHover = effectiveNoHover.value || props.noHover || isToggled.value || props.hoverDecoration === 'none'
    const isGhost = effectiveVariant.value === 'ghost'
    const useUnderline = props.hoverDecoration === 'underline'

    const styleClasses = isToggled.value
        ? getPressedClasses({ variant: effectiveVariant.value, theme: props.theme })
        : getVariantClasses({
            variant: effectiveVariant.value,
            theme: props.theme,
            disabled: isDisabled.value,
        })

    const transitionClasses = getTransitionClasses({
        variant: effectiveVariant.value,
        hoverStyle: effectiveHoverStyle.value,
        theme: props.theme,
        noHover,
    })

    const backgroundHoverClasses = (useUnderline || props.hoverDecoration === 'none') ? [] : getBackgroundHoverClasses({
        variant: effectiveVariant.value,
        hoverStyle: effectiveHoverStyle.value,
        theme: props.theme,
        noHover,
    })

    const activePressedClasses = !props.toggle && !props.value
        ? getActivePressedClasses({ variant: effectiveVariant.value, theme: props.theme })
        : []

    return [
        {
            ...styleClasses,
            'px-4': true, // Always apply horizontal padding
            '-mx-4': isGhost, // Negative margin for ghost to align with text while keeping padding
            'justify-between': props.justify === 'between',
            'justify-center text-center': props.justify === 'center',
            'justify-start': props.justify === 'start',
            'cursor-pointer': !isDisabled.value,
            'underline-offset-4': useUnderline,
            'hover:underline': useUnderline && !noHover,
            [props.class || '']: Boolean(props.class),
        },
        transitionClasses,
        backgroundHoverClasses,
        activePressedClasses,
    ]
})

const arrowClasses = computed(() => {
    if (props.arrowColor === 'primary') {
        const base = 'w-3.5 h-3.5 -rotate-45 group-hover/ui-button:rotate-0 group-hover/buttons-container:rotate-0 se-transition-all'
        if (props.theme === 'dark') {
            return `${base} text-se-primary group-hover/ui-button:text-se-white group-hover/buttons-container:text-se-white`
        }
        return `${base} text-se-primary group-hover/ui-button:text-se-dark group-hover/buttons-container:text-se-dark`
    }
    return getArrowClasses({
        variant: effectiveVariant.value,
        hoverStyle: effectiveHoverStyle.value,
        theme: props.theme,
    })
})

defineSlots<{
    before_label: () => VNode
    after_label: () => VNode
}>()
</script>

<template>
    <component 
        :is="isLink ? UiNavLink : 'button'"
        :to="isLink && !isExternal ? props.href : undefined"
        :href="isLink ? props.href : undefined"
        :external="isExternal"
        :target="isLink ? props.target : undefined"
        :rel="isLink ? props.rel : undefined"
        :allow-referrer="isLink ? props.allowReferrer : undefined"
        :type="!isLink ? 'button' : undefined"
        :disabled="!isLink && isDisabled"
        :aria-pressed="(props.toggle || props.value !== undefined) ? isToggled : undefined"
        class="group/ui-button inline-flex items-center gap-1.5 py-2 rounded-md font-semibold text-se-dark se-max-w-full-nested"
        :class="dynamicClasses"
        @click="handleClick"
        @mouseenter="handleMouseEnter"
    >
        <slot name="before_label">
            <UiIcon v-if="props.icon" :name="props.icon" class="w-4 h-4" />
        </slot>

        <UiText v-if="props.label" as="button" :variant="textVariant" no-wrap :text="label" />

        <UiIcon 
            v-if="showArrow" 
            name="arrow-right"
            :class="arrowClasses"
        />

        <slot name="after_label" />
    </component>
</template>
